// src/Services/announcementService.js
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  increment,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";
import authService from "./authService";

class AnnouncementService {
  // Check if an announcement is currently within its active window
  isAnnouncementActive(announcement) {
    if (!announcement.isActive) return false;

    const now = new Date();

    if (announcement.isRecurring) {
      const month = announcement.recurringMonth; // 1-12
      const day = announcement.recurringDay;     // 1-31
      const duration = announcement.recurringDuration || 1;

      const year = now.getFullYear();
      const startDate = new Date(year, month - 1, day, 0, 0, 0);
      const endDate = new Date(year, month - 1, day + duration, 0, 0, 0);

      return now >= startDate && now < endDate;
    } else {
      // No dates set → show indefinitely
      if (!announcement.startDate && !announcement.endDate) return true;

      const start = announcement.startDate?.toDate?.() || new Date(announcement.startDate);
      const end = announcement.endDate?.toDate?.() || new Date(announcement.endDate);
      return now >= start && now < end;
    }
  }

  // Fetch all isActive announcements, filter by date client-side
  async getActiveAnnouncements() {
    try {
      const q = query(collection(db, "announcements"), where("isActive", "==", true));
      const snap = await getDocs(q);
      const all = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      return all
        .filter((a) => this.isAnnouncementActive(a))
        .sort((a, b) => {
          const at = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bt = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bt - at;
        });
    } catch (error) {
      console.error("Error fetching active announcements:", error);
      return [];
    }
  }

  // Claim the coin reward for an announcement (signed-in users only)
  async claimReward(announcementId, coinAmount) {
    const user = authService.auth.currentUser;
    if (!user || user.isAnonymous) {
      throw new Error("Must be signed in to claim rewards");
    }

    const userRef = doc(db, "users", user.uid);
    const rewardRecord = {
      amount: coinAmount,
      timestamp: new Date(),
      type: "announcement_reward",
      announcementId,
      price: 0,
      method: "announcement_claim",
    };

    // Atomic write: record claim + increment balance + log to history
    // Does NOT set lastPurchaseAt — this is not a real purchase
    await updateDoc(userRef, {
      [`claimedAnnouncements.${announcementId}`]: new Date(),
      coinBalance: increment(coinAmount),
      coinPurchaseHistory: arrayUnion(rewardRecord),
    });

    return { success: true };
  }

  // ── Admin CRUD ───────────────────────────────────────────

  async getAllAnnouncements() {
    try {
      const q = query(collection(db, "announcements"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
    } catch (error) {
      console.error("Error fetching all announcements:", error);
      return [];
    }
  }

  async createAnnouncement(data) {
    return addDoc(collection(db, "announcements"), {
      ...data,
      createdAt: new Date(),
    });
  }

  async updateAnnouncement(id, data) {
    return updateDoc(doc(db, "announcements", id), data);
  }

  async deleteAnnouncement(id) {
    return deleteDoc(doc(db, "announcements", id));
  }
}

export default new AnnouncementService();
